/* ============
 * Edit Page
 * ============
 *
 * This is the page for editing bill and credit note.
 */
import helpers from './../../../../utils/helpers';
import moment from 'moment';
import { mapGetters, mapMutations } from 'vuex';
import store from './../../../../store';
import accountService from './../../../../services/account';
import billTypeService from './../../../../services/billType';
import billStatusService from './../../../../services/billStatus';
import orderProductService from './../../../../services/orderProduct';
import orderService from './../../../../services/order';
import modulesService from './../../../../services/modules';
import billService from './../../../../services/bill';
import invoiceService from './../../../../services/invoice';
import creditNoteService from './../../../../services/creditNote';
import countryService from './../../../../services/country';
import Forms from './../../../../utils/forms/forms';
import DateMixin from './../../../../mixins/date';
import previewMessage from './../../../../components/previewMessage/previewMessage.vue';
import previewMessageCopy from './../../../../components/previewMessageCopy/previewMessageCopy.vue';
import previewMessageReminder from './../../../../components/previewMessageReminder/previewMessageReminder.vue';
import creditNoteReason from './../../../../components/creditNoteReason/creditNoteReason.vue';
import ehfModuleSetup from '../../../../components/ehfModuleSetup/ehfModuleSetup.vue';
import billPayment from './../../../../components/billPayment/billPayment.vue';
import billReminderModal from './../../../../components/billReminderModal/billReminderModal.vue';

export default {
  data() {
    return {
      form: new Forms({
        orderId: {
          value: '',
          type: 'autocomplete',
          attributes: {
            isSearch: true,
            searchMethod: this.searchMethod,
            loading: true,
          },
        },
        billStatusId: {
          value: '',
          type: 'hidden',
        },
        date: {
          value: '',
          type: 'date',
          attributes: {
            datePickerOptions: {
              disabledDate: this.disabledDate,
            },
          },
        },
        dueDate: {
          value: '',
          type: 'date',
        },
        languageId: {
          value: '',
          type: 'select',
        },
        currencyId: {
          value: '',
          type: 'select',
        },
        paymentDate: {
          value: '',
          type: 'date',
        },
        summaryGrouping: {
          value: '',
          type: 'checkbox',
        },
        creditNoteSource: {
          value: '',
          type: 'hidden',
        },
        kidNumber: {
          value: '',
          type: 'text',
        },
        paymentMessage: {
          value: '',
          type: 'textarea',
        },
        note: {
          value: '',
          type: 'textarea',
        },
      }),
      successMessage: '',
      loadingCreditNote: false,
      sendLoading: false,
      sendEHFLoading: false,
      kidLoading: false,
      loading: false,
      hashid: '',
      roundInvoiceTotal: true,
      tabIndex: '1',
      dropdownState: false,
      invoiceSentAt: '',
      creditNotes: [],
      attachmentFiles: [],
      billType: '',
      previewWait: '',
      billAddressData: [],
      emailTrackingRecipients: [],
      emailCopy: [],
      emailCopyClass: ['active'],
      submitLoading: false,
      sendReminderPreviewLoading: false,
    };
  },

  computed: {
    ...mapGetters({
      orders: 'allOrders',
      billTypes: 'billTypes',
      billStatuses: 'billStatuses',
      currentBill: 'currentBill',
      account: 'account',
      orderProducts: 'allOrderProducts',
      billProducts: 'allBillProducts',
      currentOrder: 'currentOrder',
      settings: 'organizationSettings',
      modules: 'allModules',
      countries: 'allCountries',
      languages: 'languages',
      currencies: 'currencies',
    }),

    currency() {
      return this.currentBill.currency ? this.currentBill.currency.code : '';
    },

    billReminders() {
      return this.currentBill.billReminders;
    },

    billReminderTotalAmount() {
      return this.currentBill.reminderFeesTotalAmount;
    },

    showCreateReminderButton() {
      // Aligned with canSendPaymentReminderActions: only when overdue and e-mail/EHF path can receive reminder mail.
      return !this.isCreditNote
        && this.showOverdueInvoiceNotice
        && this.canSendPaymentReminderByEmail
        && this.billProducts.length > 0
        && typeof this.currentOrder !== 'undefined'
        && !this.isEdiOrder;
    },

    emailCopies() {
      return this.emailCopy;
    },

    hasEHFModule() {
      if (this.account.modules.length > 0) {
        return this.account.modules.find(module => module.name === 'EHF');
      }
      return false;
    },

    /**
     * Client billing method (EMAIL | EHF) from DB; defaults to EMAIL when unset.
     */
    invoiceSendMethodNormalized() {
      const m = this.currentBill && this.currentBill.client && this.currentBill.client.invoiceSendMethod;
      if (m === undefined || m === null || m === '') {
        return 'EMAIL';
      }
      return String(m).toUpperCase();
    },

    invoiceSendMethodIsEmail() {
      return this.invoiceSendMethodNormalized === 'EMAIL';
    },

    invoiceSendMethodIsEhf() {
      return this.invoiceSendMethodNormalized === 'EHF';
    },

    /**
     * Payment reminders are sent by email; allow when customer has email whether the invoice was sent by EMAIL or EHF.
     */
    canSendPaymentReminderByEmail() {
      if (!this.currentClient || !this.currentClient.email) return false;
      return this.invoiceSendMethodIsEmail || this.invoiceSendMethodIsEhf;
    },

    /**
     * EHF is the customer's chosen method but client_settings.ehf_billing_3_0 is off (e.g. Peppol not persisted).
     */
    ehfInvoicePreferredButNotVerified() {
      return this.invoiceSendMethodIsEhf && !this.currentClient.ehfBilling3_0;
    },

    /**
     * Show warning + offer email fallback while EHF is blocked.
     * Only when EHF module is active — otherwise show module upsell first.
     */
    showEhfPendingVerificationBanner() {
      if (!this.hasEHFModule) return false;
      if (typeof this.currentOrder === 'undefined') return false;
      if (this.isEdiOrder) return false;
      if (!this.isPending) return false;
      return this.ehfInvoicePreferredButNotVerified;
    },

    /**
     * Customer is verified for EHF in Peppol, but invoice send method is still email — suggest EHF first.
     */
    showEhfPreferredOverEmailBanner() {
      if (!this.hasEHFModule) return false;
      if (typeof this.currentOrder === 'undefined') return false;
      if (this.isEdiOrder) return false;
      if (!this.isPending) return false;
      return this.invoiceSendMethodIsEmail && !!this.currentClient.ehfBilling3_0;
    },

    /**
     * EHF module not on the account — upsell first. Shown when customer is Peppol-verified
     * or has EHF as invoice method (must activate module before app can send/verify EHF).
     */
    showEhfModuleUpsellBanner() {
      if (this.hasEHFModule) return false;
      if (!this.currentClient) return false;
      if (typeof this.currentOrder === 'undefined') return false;
      if (this.isEdiOrder) return false;
      if (!this.isPending) return false;
      return !!(this.currentClient.ehfBilling3_0 || this.invoiceSendMethodIsEhf);
    },

    /** Upsell body: customer verified in Peppol vs EHF method only (Peppol not registered yet). */
    ehfModuleUpsellCustomerVerified() {
      return !!(this.currentClient && this.currentClient.ehfBilling3_0);
    },

    /**
     * Allow sending by email when EHF is preferred but not verified, if customer has an email.
     */
    showSendInvoiceEmailFallbackForEhf() {
      return this.ehfInvoicePreferredButNotVerified && !!this.currentClient.email && !this.isEdiOrder;
    },

    /**
     * Custom message if excise and inventory modules is active.
     */
    returnWarning() {
      let hasExciseModule = false;
      let hasInventoryModule = false;
      if (this.account.modules.length > 0) {
        for (let i = 0; i < this.account.modules.length; i += 1) {
          if (this.account.modules[i].name === 'Excise') {
            hasExciseModule = true;
          }
          if (this.account.modules[i].name === 'Inventory') {
            hasInventoryModule = true;
          }
        }
        let message = '';
        if (hasExciseModule && hasInventoryModule && !this.isCleared) {
          message = `<p>&#8226;&nbsp;${this.$t('creditNoteWarning.invoiceInfo.exciseInventoryActivated')}</p>`; // 'Du har særavgift og lagermodul aktivert på din konto. Husk å fullføre kreditnotaen for å registrere evt. returer!';
        } else if (hasExciseModule && !this.isCleared) {
          message = `<p>&#8226;&nbsp;${this.$t('creditNoteWarning.invoiceInfo.specialChargeActivated')}</p>`; // 'Du har særavgift aktivert på din konto. Husk å fullføre kreditnotaen for å registrere evt. returer!';
        } else if (hasInventoryModule && !this.isCleared) {
          message = `<p>&#8226;&nbsp;${this.$t('creditNoteWarning.invoiceInfo.storageModuleEnabled')}</p>`; // 'Du har lagermodul aktivert på din konto. Husk å fullføre kreditnotaen for å registrere evt. returer!';
        }
        if (!this.currentClient.email && !this.isEdiOrder) {
          message += `<p>&#8226;&nbsp;${this.$t('creditNoteWarning.invoiceInfo.noClientEmail')}</p>`;
        }
        if (message !== '') {
          return message;
        }
      }
      return false;
    },

    /**
     * Checks if it is the credit note route.
     */
    isCreditNote() {
      return this.$route.name === 'bills.creditNote';
    },

    /**
     * Check if it is still a draft.
     */
    isDraft() {
      return this.currentBill.billStatus === 'Draft';
    },

    /**
     * Check if it is still pending.
     */
    isPending() {
      return this.currentBill.billStatus === 'Pending';
    },

    /**
     * Check if it is sent.
     */
    isSent() {
      return this.currentBill.billStatus === 'Sent';
    },

    /**
     * Check if it is cleared.
     */
    isCleared() {
      return this.currentBill.billStatus === 'Cleared';
    },

    /**
     * Gets the edi of order
     */
    currentEdiOrder() {
      if (this.currentOrder.ediOrder) {
        return this.currentOrder.ediOrder;
      }

      return {};
    },

    /**
     * Check if it is partly paid.
     */
    isPartlyPaid() {
      return this.currentBill.billStatus === 'Partly Paid';
    },

    /**
     * True when calendar due date has passed (matches backend Carbon::now()->gt(due_date)).
     */
    isPastDueDate() {
      if (!this.currentBill || !this.currentBill.dueDate) return false;
      if (!moment(this.currentBill.dueDate).isValid()) return false;
      return moment().isAfter(moment(this.currentBill.dueDate).startOf('day'));
    },

    billStatusAllowsPaymentReminder() {
      const s = this.currentBill.billStatus;
      return ['Sent', 'Overdue', 'Partly Paid'].includes(s);
    },

    /**
     * Show prominent notice when invoice is unpaid and past due (not credit note).
     */
    showOverdueInvoiceNotice() {
      return !this.isCreditNote
        && this.isPastDueDate
        && this.billStatusAllowsPaymentReminder
        && !this.isCleared;
    },

    /**
     * Highlight due date when the invoice is past due and awaiting payment.
     */
    invoiceDueDateIsOverdue() {
      return this.showOverdueInvoiceNotice;
    },

    /**
     * Show "Send påminnelse" / reminder PDF when overdue; backend does not require existing BillReminder rows.
     */
    canSendPaymentReminderActions() {
      return this.showOverdueInvoiceNotice
        && this.canSendPaymentReminderByEmail
        && typeof this.currentOrder !== 'undefined'
        && !this.isEdiOrder;
    },

    hasCreditNoteReason() {
      return this.currentBill.creditNoteReason;
    },

    /**
     * The current client for the order.
     */
    currentClient() {
      const client = this.currentBill.client;
      if (this.currentOrder.orderType === 'EDI') {
        client.streetName = this.currentEdiOrder.ediStoreStreet || client.streetName;
        client.postalCode = this.currentEdiOrder.ediStorePostCode || client.postalCode;
        client.billAddressData = this.billAddressData[0];
        return client;
      }
      client.billAddressData = this.billAddressData[0];
      return client;
    },

    curentOrderType() {
      if (typeof this.currentOrder.orderType === 'undefined') {
        return '';
      }
      return this.currentOrder.orderType;
    },

    /**
     * True when the bill is tied to an EDI order. Uses orderType and fallbacks
     * (ediOrderId / ediOrder) so we do not treat EDI as non-EDI when orderType is missing in the API.
     */
    isEdiOrder() {
      if (typeof this.currentOrder === 'undefined' || !this.currentOrder) {
        return false;
      }
      const orderTypeNorm = String(this.currentOrder.orderType || '').trim().toLowerCase();
      if (orderTypeNorm === 'edi') {
        return true;
      }
      const ediOrderIdNum = Number(this.currentOrder.ediOrderId);
      if (ediOrderIdNum > 0) {
        return true;
      }
      // simpleOrder API has no nested edi_order; EDI is indicated by edi_type / edi_store from joins.
      if (String(this.currentOrder.ediType || '').trim() !== '') {
        return true;
      }
      if (String(this.currentOrder.ediStore || '').trim() !== '') {
        return true;
      }
      if (String(this.currentOrder.ediStatus || '').trim() !== '') {
        return true;
      }
      const eo = this.currentOrder.ediOrder;
      if (eo && typeof eo === 'object' && !Array.isArray(eo) && eo.id) {
        return true;
      }
      if (Array.isArray(eo) && eo.length > 0) {
        return true;
      }
      return false;
    },

    currentOrdeStatus() {
      if (typeof this.currentBill.billStatus === 'undefined') {
        return '';
      }
      return this.currentBill.billStatus;
    },

    /**
     * Determines if the credit note source is badly missing.
     */
    /* eslint max-len: ["error", 150] */
    noCreditNoteSource() {
      const eo = this.currentEdiOrder;
      if (eo && (eo.ediOrderTypeId === 2 || eo.ediOrderTypeId === 3)) {
        return false;
      }

      if (this.currentBill.creditNoteSource > 0 && (this.currentOrder.orderTypeId === 1 || (eo && eo.ediOrderTypeId === 1))) {
        return true;
      }

      return false;
    },

    /**
     * If client contact email is missing
     * Add warning
     */
    isClientContactEmailMissing() {
      if (this.invoiceSendMethodIsEhf) {
        return false;
      }
      if (!this.isEdiOrder && (this.currentClient.email === null || this.currentClient.email === '')) {
        return true;
      }
      return false;
    },

    /**
     * Checks if it is the invoiceroute.
     */
    isInvoice() {
      return this.$route.name === 'bills.edit';
    },

    hasTripletexModule() {
      if (this.account.modules.length > 0) {
        if (this.account.modules.find(module => module.name === 'Tripletex')) return true;
      }
      return false;
    },
  },

  watch: {

    $route() {
      this.toggleDueDate();
      this.togglePaymentDate();
      this.show();
      this.urlCurrentTab();
    },

    tabIndex(value) {
      this.assignTab(value);
    },

    currentOrder(currentOrder) {},
    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} billTypes    The billTypes list.
     */
    billTypes(billTypes) {
      this.form.setOptions('billTypeId', billTypes);
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} billStatuses    The billStatuses list.
     */
    billStatuses(billStatuses) {
      this.form.setOptions('billStatusId', billStatuses.map(s => ({ id: s.id, name: s.status })));
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} orders    The orders list.
     */
    orders(orders) {
      this.form.setOptions('orderId', orders.all.map(o => ({ id: o.id, name: o.number })));
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} currentBill    The bill.
     */
    currentBill(currentBill) {
      this.form.assignData(currentBill);
      this.disableIfSent();
      this.togglePaymentDate();

      if (currentBill.billStatus !== 'Pending' && currentBill.billStatus !== 'Draft') {
        this.form.disableField('languageId');
        this.form.disableField('currencyId');
      }
    },

    settings(settings) {
      if (!settings.paymentIdentitificationGeneration) {
        this.form.hideField('kidNumber');
      } else {
        this.form.showField('kidNumber');
      }
    },

    'form.kidNumber': function (kidNumber) {
      if (this.currentBill.billStatus == 'Draft') {
        this.form.setExtraAttr('kidNumber', {});
        return;
      }
      if (kidNumber) {
        this.form.setExtraAttr('kidNumber', {
          hasButton: true,
          buttonLabel: this.$t('static.bills.edit.validateKidNumber'),
          buttonHandler: this.validateKidNumber,
        });
      } else {
        this.form.setExtraAttr('kidNumber', {
          hasButton: true,
          buttonLabel: this.$t('static.bills.edit.generateKidNumber'),
          buttonHandler: this.generateKidNumber,
        });
      }
    },

    languages(value) {
      this.form.setOptions('languageId',
        value.all.map(item => ({
          id: item.id,
          name: item.name,
        })));
    },

    currencies(value) {
      this.form.setOptions('currencyId',
        value.all.map(item => ({
          id: item.id,
          name: item.name,
        })));
    },
  },

  mounted() {
    this.urlCurrentTab();
    this.assignTab(this.tabIndex);
  },

  created() {
    // close dropdown if click outside
    const self = this;
    window.addEventListener('click', (e) => {
      if (helpers.closeDropdown(e, self.dropdownState) === false) self.dropdownState = false;
    });
    this.toggleDueDate();
    this.togglePaymentDate();
    this.show();
  },

  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalSize: 'SET_MODAL_SIZE',
      setModalProps: 'SET_MODAL_PROPS',
      setModalButtonLoading: 'SET_MODAL_BUTTON_LOADING',
    }),

    sendReminderPreview() {
      this.sendReminderPreviewLoading = true;
      billService.sendReminderPreview(store.state.route.params.id)
        .then((response) => {
          this.setModalContent(() => previewMessageReminder);
          this.setModalSize('full');
          this.setModalProps({
            message: response.message,
            subject: response.subject,
            to: [response.to],
            cc: [],
            buttons: [{
              name: this.$t('button.cancel'),
              action: this.cancelModal,
              title: this.$t('button.cancel'),
            },
            {
              name: this.$t('static.bills.edit.sendReminder'),
              action: this.sendReminder,
              title: this.$t('static.bills.edit.sendReminder'),
            }],

          });
          this.sendReminderPreviewLoading = false;
          this.toggleModal(true);
        })
        .catch((error) => {
          this.sendReminderPreviewLoading = false;
          console.log(error);
        });
    },

    sendReminder(data) {
      this.toggleModal(false);
      this.previewWait = 'Sending reminder';
      billService.sendReminder(store.state.route.params.id, data)
        .then((response) => {
          if (response.success) {
            this.$message({
              showClose: true,
              message: 'Successfully sent',
              type: 'success',
            });
          } else {
            this.$message({
              showClose: true,
              message: response.errors[0].message,
              type: 'error',
            });
          }
          this.previewWait = '';
          this.sendLoading = false;
        })
        .catch(() => {
          this.$message({
            showClose: true,
            message: 'Something went wrong!',
            type: 'error',
          });
          this.previewWait = '';
          this.sendLoading = false;
        });
    },

    createBillReminder() {
      this.setModalContent(() => billReminderModal);
      this.setModalSize('full');
      this.setModalProps({
        bill: this.currentBill,
        onComplete: this.refreshBill,
        onClose: this.closeModal,
      });
      this.toggleModal(true);
    },

    refreshBill() {
      billService.show(store.state.route.params.id);
      orderService.all();
      setTimeout(() => {
        this.tabIndex = '4';
      }, 1500);
    },

    closeModal() {
      this.form.loading = '';
    },

    billPayment() {
      this.setModalContent(() => billPayment);
      this.setModalSize('full');
      this.setModalProps({
        billId: this.currentBill.id,
        onSave: this.save,
        onClose: this.closeModal(),
        billingNumber: this.currentBill.billingNumber ? `#${this.currentBill.billingNumber}` : '',
        additionalData: {
          due_date: this.isValidDate(this.currentBill.dueDate) ? this.formatDate(this.currentBill.dueDate) : '-',
          total: this.currentBill.totalGross,
          paid: this.currentBill.amountPaid,
          due: this.currentBill.amountDue.toFixed(2),
        },
      });
      this.toggleModal(true);
    },

    previewMailTracking(value) {
      const arrayTo = [];
      const arrayCc = [];
      value.recipient.forEach((item) => {
        if (item.email_recipient_type.type === 'to') {
          arrayTo.push(item.email_address);
        } else if (item.email_recipient_type.type === 'cc') {
          arrayCc.push(item.email_address);
        }
      });
      this.setModalContent(() => previewMessageCopy);
      this.setModalSize('full');
      this.setModalProps({
        message: value.body,
        subject: value.subject,
        to: arrayTo,
        cc: arrayCc,
      });
      this.toggleModal(true);
    },

    recipients(value, key) {
      this.emailCopyClass = [];
      this.emailCopyClass[key] = 'active';
      this.emailTrackingRecipients = value;
    },

    assignTab(value) {
      let tab = '';
      if (value === '4') {
        tab = '#reminders';
      } else if (value === '3') {
        tab = '#emails';
        billService.show(store.state.route.params.id)
        .then((data) => {
          this.emailCopy = data.emailCopy;
        });
      } else if (value === '2') {
        tab = '#editInvoice';
      } else {
        tab = '#invoiceInformation';
      }
      history.pushState({}, null, `/#${this.$route.path}${tab}`);
    },

    urlCurrentTab() {
      if (this.$router.currentRoute.hash === '#reminders') {
        this.tabIndex = '4';
      } else if (this.$router.currentRoute.hash === '#emails') {
        this.tabIndex = '3';
      } else if (this.$router.currentRoute.hash === '#editInvoice') {
        this.tabIndex = '2';
      } else {
        this.tabIndex = '1';
      }
    },

    /**
     * Show and hide due date field.
     */
    toggleDueDate() {
      if (this.isCreditNote) {
        this.form.hideField('dueDate');
      } else {
        this.form.showField('dueDate');
      }
    },

    /**
     * Show and hide payment date field.
     */
    togglePaymentDate() {
      if (!this.isCreditNote && this.isCleared) {
        this.form.showField('paymentDate');
      } else {
        this.form.hideField('paymentDate');
      }
    },

    submitCreditNoteReason() {
      this.setModalContent(() => creditNoteReason);
      this.setModalSize('full');
      this.setModalProps({
        onClose: this.closeModal(),
        billId: this.currentBill.id,
        billStatusId: this.form.billStatusId,
        orderId: this.currentBill.orderId,
        date: this.currentBill.date,
        dueDate: this.currentBill.dueDate,
        creditNoteSource: this.currentBill.creditNoteSource,
      });
      this.toggleModal(true);
    },

    markAsCleared() {
      /* Error when marking as paid due to "payment date must be a date after the invoice date"
       * Solution: Add 1 hour to this time.
       */
      this.form.paymentDate = moment().add(1, 'h').format();
      if (this.isCreditNote) {
        this.save('Cleared');
      } else {
        this.billPayment();
      }
    },

    /**
     * Adds the bill status to the form before saving.
     *
     * @param {String} name The status name.
     */
    save(name, data = null) {
      // If credit note, open modal to record reason for crediting.
      this.submitLoading = true;
      this.form.loading = name;
      const status = this.billStatuses.filter(s => (
          s.status === name
        ))[0];
      this.form.billStatusId = status.id;
      if (!this.isCreditNote && name === 'Cleared') {
        billService.markAsPaid(store.state.route.params.id, { order_id: this.currentBill.orderId, payment_date: data ? data.paymentDate : this.form.paymentDate })
        .then(() => {
          this.form.loading = '';
        });
      } else {
        this.update();
      }
    },

    /**
     * Method to update the bill.
     */
    update() {
      if (this.isCreditNote) {
        this.form.setFieldValue('orderId', this.currentOrder.id);
      }

      const service = this.isCreditNote ? creditNoteService : billService;
      service.update(store.state.route.params.id, this.form.data())
        .then(() => {
          this.successMessage = this.$t('static.bills.edit.updateSuccess');
          setTimeout(() => {
            this.successMessage = '';
          }, 2000);
          this.form.loading = '';
          this.submitLoading = false;
        }).catch((errors) => {
          if (errors.code === 40120) {
            this.$message({
              showClose: true,
              message: errors.message,
              customClass: 'error',
            });
          }
          if (errors.code === 40116) {
            errors = {
              orderId: [this.$t('static.bills.create.orderError')],
            };
          }
          this.submitLoading = false;
          this.form.loading = '';
          this.form.recordErrors(errors);
        });
    },

    /**
     * If order has been sent.
     */
    disableIfSent() {
      const fields = Object.keys(this.form.fields).filter(k => k !== 'paymentDate');
      if (this.isDraft || this.isPending) {
        fields.map(f => this.form.enableField(f));
        return;
      }

      fields.map(f => this.form.disableField(f));
    },

    /**
     * Fetches the bill object for editing.
     */
    show() {
      this.loading = true;
      const me = this;
      const requests = [
        billService.show(store.state.route.params.id),
        billTypeService.all(),
        // orderService.all(),
        billStatusService.all(),
        billService.attachmentPdf('show', store.state.route.params.id, {}),
        countryService.all(),
        accountService.languages(),
        accountService.currencies(),
      ];
      if (!this.modules.length) {
        requests.push(modulesService.items());
      }
      Promise.all(requests).then((data) => {
        this.billType = data[0].billType;
        this.creditNotes = data[0].creditNotes;
        this.hashid = data[0].hashid;
        this.roundInvoiceTotal = data[0].roundInvoiceTotal;
        this.invoiceSentAt = data[0].billSentAt;
        this.attachmentFiles = data[4];
        this.billAddressData.push({ billAddresMissing: data[0].billAddressMissing, billAddress: data[0].billAddress });
        this.emailCopy = data[0].emailCopy;
        this.emailTrackingRecipients = this.emailCopy[0] && this.emailCopy[0].tracking ? this.emailCopy[0].tracking.email_tracking_recipients : [];

        if (data[0].orderId !== null) {
          Promise.all([
            orderProductService.all(data[0].orderId),
            orderService.simpleOrder(data[0].orderId),
          ]).then(() => {
            // console.log(this.currentOrder.number);
            this.form.setFieldValue('orderId', { id: this.currentOrder.id, name: this.currentOrder.number });
            me.loading = false;
          });
        } else {
          this.form.removeField('orderId');
          me.loading = false;
        }
      });
    },

    /**
     * Preview the invoice to be sent.
     */
    previewSendInvoice() {
      /**
       * Everytime user preview invoice it will generate and query attachment
       * To make sure before sending invoice it has latest attachments.
       */
      this.previewWait = this.$t('static.bills.edit.sendPreviewInfo');
      this.sendLoading = true;
      Promise.all([
        billService.attachmentPdf('generate', store.state.route.params.id, { billType: this.billType }),
      ])
        .then(() => {
          Promise.all([
            billService.attachmentPdf('show', store.state.route.params.id, {}),
          ]).then((data) => {
            this.attachmentFiles = data[0];
            this.previewData();

            this.sendLoading = false;
          }).catch((errors) => {
            this.sendLoading = false;
          });
        })
        .catch((errors) => {
        });
    },

    previewData() {
      // console.log(this.currentClient.contacts);
      invoiceService.showBillEmailInformation(this.currentBill.id)
        .then((billEmailInformation) => {
          const cc = [billEmailInformation.userEmail];
          if (billEmailInformation.commonEmail !== null) {
            cc.push(billEmailInformation.commonEmail);
          }
          this.setModalContent(() => previewMessage);
          this.setModalSize('full');
          this.setModalProps({
            contacts: this.currentClient.contacts,
            message: billEmailInformation.message,
            subject: billEmailInformation.title,
            ccEmail: cc,
            notes: '',
            // userEmail: billEmailInformation.userEmail,
            // commonEmail: billEmailInformation.commonEmail,
            // cc: true,
            // ccCommon: true,
            server: this.attachmentServer(),
            initFiles: this.attachmentFiles,

            headerTexts: [
          //   `${this.$t('static.bills.edit.to')}: ${this.currentClient.companyName || `${this.currentClient.firstName}
          // ${this.currentClient.lastName}`} <${billEmailInformation.toEmail}>`,
              `${this.$t('static.bills.edit.from')}: ${billEmailInformation.fromName} <${billEmailInformation.fromEmail}>`,
            ],
            attachments: [{
              attachmentName: billEmailInformation.attachmentName,
              action: this.showInvoice,
            }],
            buttons: [{
              name: this.$t('button.cancel'),
              action: this.cancelModal,
              title: this.$t('button.cancel'),
            },
            {
              name: this.$t('button.editText'),
              action: this.editSettings,
              title: this.$t('button.editText'),
            },
            {
              name: (this.isPending ? this.$t('button.send') : this.$t('button.resend')),
              action: this.sendInvoice,
              title: (this.isPending ? this.$t('button.send') : this.$t('button.resend')),
            },

            ],
            links: [{
              name: this.$t('button.testEmailTitle'),
              action: this.sendTestInvoice,
            }],
          });
          this.toggleModal(true);
          this.previewWait = '';
        })
        .catch(() => {
          // Could not predict how this email will be sent.
          // Show some handling
        });
    },

    attachmentServer() {
      return {
        process: (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
          jQuery('#origAttach').remove();
          Promise.all([
            billService.attachmentPdf('exist', store.state.route.params.id, { source: file.name }),
          ]).then(() => {
            const formData = new FormData();
            formData.append(fieldName, file, file.name);
            const request = new XMLHttpRequest();
            request.open('POST', `${process.env.API_LOCATION}/bills/pdf_attachment/${this.currentBill.id}/store`);
            request.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('id_token')}`);
            request.upload.onprogress = (e) => {
              progress(e.lengthComputable, e.loaded, e.total);
            };
            request.onload = function () {
              if (request.status >= 200 && request.status < 300) {
                load();
                load(request.responseText);
              } else {
                error('oh no');
              }
            };
            request.send(formData);
            return {
              abort: () => {
                request.abort();
                abort();
              },
            };
          }).catch((errors) => {
            console.log(errors);
            jQuery('.errors.filepond div').html(`<span id="origAttach">${errors.pdfAttachment[0]}</span>`);
            error(errors.pdfAttachment[0]);
          });
        },
        revert: (uniqueFileId, load, error) => {
          jQuery('#origAttach').remove();
          Promise.all([
            billService.attachmentPdf('revert', store.state.route.params.id, { source: uniqueFileId }),
          ]).then(() => {
            load();
          }).catch((errors) => {
            console.log(errors);
          });
        },
        remove: (uniqueFileId, load, error) => {
          jQuery('#origAttach').remove();
          Promise.all([
            billService.attachmentPdf('remove', store.state.route.params.id, { source: uniqueFileId }),
          ]).then((data) => {
            load();
          }).catch((errors) => {
            error(errors.pdfAttachment[0]);
            jQuery('.errors.filepond div').html(`<span id="origAttach">${errors.pdfAttachment[0]}</span>`);
            console.log(errors.pdfAttachment[0]);
          });
        },
      };
    },

    /**
     * Cancel modal trigger
     */
    cancelModal() {
      this.toggleModal(false);
    },

    /**
     * Edit settings
     */
    editSettings() {
      this.toggleModal(false);
      this.$router.push({
        name: 'application.settings',
        params: { tab: 'email' },
      });
    },

    /**
     * Send the invoice to the client email.
     */
    sendInvoice(ev, test = false) {
      // console.log(store.state.modal.modalProps.email_cc);
      this.sendLoading = true;
      this.setModalButtonLoading(true);
      invoiceService.send(
          store.state.route.params.id, {
            email_to: store.state.modal.modalProps.email_to,
            email_cc: store.state.modal.modalProps.email_cc,
            message: store.state.modal.modalProps.message,
            subject: store.state.modal.modalProps.subject,
            // cc: store.state.modal.modalProps.cc,
            // ccCommon: store.state.modal.modalProps.ccCommon,
            notes: store.state.modal.modalProps.notes,
            test,
          })
        .then(() => {
          this.toggleModal(false);
          this.sendLoading = false;
          this.setModalButtonLoading(false);
          billService.show(store.state.route.params.id)
          .then((response) => {
            this.emailCopy = response.emailCopy;
            this.emailTrackingRecipients = this.emailCopy[0] ? this.emailCopy[0].tracking.email_tracking_recipients : [];
          });
          orderService.all();
          this.$message({
            showClose: true,
            message: this.$t('static.bills.edit.sendInvoiceSuccess'),
            type: 'success',
          });
        }).catch((response) => {
          this.toggleModal(false);
          this.sendLoading = false;
          this.setModalButtonLoading(false);
          this.$message({
            showClose: true,
            message: (response.data.message || response.data.error.message) || this.$t('static.bills.edit.sendInvoiceError'),
            type: 'error',
          });
        });
    },

    sendEHFInvoice() {
      this.sendEHFLoading = true;
      this.setModalContent(() => component);

      if (!this.hasEHFModule) {
        this.showEHFModuleModal();
        this.sendEHFLoading = false;
        return;
      }
      invoiceService.sendEHF(
          store.state.route.params.id
        )
        .then(() => {
          this.sendEHFLoading = false;
          billService.show(store.state.route.params.id);
          orderService.all();
          this.$message({
            showClose: true,
            message: this.$t('static.bills.edit.sendInvoiceSuccess'),
            type: 'success',
          });
        }).catch((response) => {
          this.sendEHFLoading = false;
          this.$message({
            showClose: true,
            message: this.$t('static.bills.edit.sendInvoiceError'),
            type: 'error',
          });
        });
    },

    showEHFModuleModal() {
      const module = this.modules.find(m => m.name === 'EHF');
      if (!module) {
        this.$message({
          showClose: true,
          message: this.$t('static.bills.edit.ehfModuleUpsellUnavailable'),
          type: 'warning',
        });
        return;
      }
      this.setModalProps({ module });
      this.toggleModal(true);
      this.setModalContent(() => ehfModuleSetup);
    },

    /**
     * Send a test invoice to user email
     */

    sendTestInvoice(ev) {
      this.sendInvoice(ev, true);
    },

    /**
     * Send the EDI invoice to the FTP.
     */
    sendBillEDI() {
      const eo = this.currentOrder.ediOrder;
      const nestedId = eo && typeof eo === 'object' && !Array.isArray(eo) && eo.id
        ? Number(eo.id)
        : 0;
      const ediId = Number(this.currentOrder.ediOrderId) || nestedId;
      if (!ediId || ediId <= 0) {
        this.$message({
          showClose: true,
          message: this.$t('static.bills.edit.sendInvoiceError'),
          customClass: 'error',
        });
        return;
      }
      this.sendLoading = true;
      invoiceService.sendEDI(store.state.route.params.id, ediId)
        .then((data) => {
          if (data.xml) {
            this.$msgbox({
              title: 'XML',
              message: data.xml,
              customClass: 'xmlModalCustomClass',
            });
          } else {
            this.$message({
              showClose: true,
              message: this.$t('static.bills.edit.sendInvoiceSuccess'),
              type: 'success',
            });
          }
          billService.show(store.state.route.params.id);
        }).catch(() => {
          this.sendLoading = false;
          this.$message({
            showClose: true,
            message: this.$t('static.bills.edit.sendInvoiceError'),
            customClass: 'error',
          });
        });
    },

    /**
     * Show the invoice pdf in another window.
     */
    showInvoice(reminderFee = false) {
      if (this.isCreditNote) {
        creditNoteService.show(this.hashid);
        return;
      }
      invoiceService.show(this.hashid, reminderFee);
    },

    downloadInvoice() {
      if (this.isCreditNote) {
        creditNoteService.download(this.hashid);
        return;
      }
      invoiceService.download(this.hashid);
    },

    /**
     * Redirects to credit note creation page.
     */
    createCreditNote() {
      this.loadingCreditNote = true;
      creditNoteService.store(store.state.route.params.id)
        .then((bill) => {
          this.$router.replace({
            name: 'bills.creditNote',
            params: {
              id: bill.id,
            },
          });
          window.scrollTo(0, 0);
          this.loadingCreditNote = false;
        }).catch(() => {
          this.$message({
            showClose: true,
            message: this.$t('static.bills.edit.createNoteError'),
            type: 'error',
          });
          this.loadingCreditNote = false;
        });
    },

    /**
     * Updates the current bill round invoice total
     **/
    roundInvoice() {
      billService.updateRoundInvoiceTotal(store.state.route.params.id, this.roundInvoiceTotal)
        .then(() => {
          this.successMessage = this.$t('static.bills.edit.updateSuccess');
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        }).catch(() => {});
    },

    /**
     * Delete specific bill.
     *
     * @param  {Object} bill The bill object.
     */
    deleteBill(bill) {
      this.$confirm(this.$t('static.bills.index.confirmDelete'), this.$t('static.bills.index.confirmDeleteTitle'), {
        confirmButtonText: this.$t('button.delete'),
        cancelButtonText: this.$t('button.cancel'),
        type: 'warning',
      }).then(() => {
        billService.destroy(bill.id).then(() => this.$router.push({ name: 'bills.index' }))
          .catch(({ response }) => {
            let message = this.$t('static.bills.index.deleteError');
            if (response.data.error && response.data.error.code === 40117) {
              message = this.$t('static.bills.index.deleteNotDraftError');
            }
            this.$message({
              showClose: true,
              message,
              customClass: 'error',
            });
          });
      }).catch(() => {});
    },

    toggleDropdown() {
      this.dropdownState = !this.dropdownState;
    },
    searchMethod(search = '') {
      return Promise.all([
        orderService.search({ search, limit: 10 }),
      ]);
    },
    validateKidNumber() {
      this.kidLoading = true;
      billService.validateKID(this.currentBill.id, this.form.kidNumber).then(() => {
        this.kidLoading = false;
        this.$message({
          showClose: true,
          message: this.$t('static.bills.edit.validKID'),
        });
      }).catch(() => {
        this.kidLoading = false;
        this.$message({
          showClose: true,
          message: this.$t('static.bills.edit.invalidKID'),
          customClass: 'error',
        });
      });
    },
    generateKidNumber() {
      this.kidLoading = true;
      billService.generateKID(this.currentBill.id).then(() => {
        this.kidLoading = false;
        this.$message({
          showClose: true,
          message: this.$t('static.bills.edit.kidGenerateSuccess'),
        });
      }).catch(() => {
        this.kidLoading = false;
        this.$message({
          showClose: true,
          message: this.$t('static.bills.edit.invalidKID'),
          customClass: 'error',
        });
      });
    },
    disabledDate(date) {
      date.setDate(date.getDate() + 15);
      return date < new Date();
    },
  },

  components: {
    VForm: require('./../../../../components/form/form.vue'),
    VMessage: require('./../../../../components/form/message/message.vue'),
    VProductsTable: require('./../../../../components/billProductsTable/billProductsTable.vue'),
    VEdiOrderItemsTable: require('./../../../../components/ediOrderItemsTable/ediOrderItemsTable.vue'),
    VFormTable: require('./../../../../components/formTable/formTable.vue'),
    VCreditNoteTable: require('./../../../../components/creditNoteProductsTable/creditNoteProductsTable.vue'),
    VClientInfo: require('./../../../../components/clientInfo/clientInfo.vue'),
  },

  mixins: [
    DateMixin,
  ],
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/bills/edit/edit.js